import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send, Bot, FileSearch } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

interface Passage { title: string; url: string | null; content: string; score: number; }
interface PreviewResult {
  reply: string;
  passages: Passage[];
  provider: string;
  model: string;
  embeddings_available: boolean;
}

export function ChatbotPlayground() {
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<PreviewResult | null>(null);

  const test = useMutation({
    mutationFn: async (msg: string) => {
      const res = await api.post('/ai/chatbot/test', { message: msg });
      return res.data.data as PreviewResult;
    },
    onSuccess: (data) => setResult(data),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (message.trim()) test.mutate(message.trim());
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Probar el asistente</CardTitle>
          <CardDescription>Simula una pregunta de un visitante usando la configuración y el conocimiento actuales (no guarda conversación ni leads).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={submit} className="flex gap-2">
            <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="¿Qué servicios de SEO ofrecéis?" />
            <Button type="submit" disabled={test.isPending || !message.trim()}>
              <Send className="size-4" /> {test.isPending ? '…' : 'Enviar'}
            </Button>
          </form>

          {result && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">Proveedor: {result.provider}</Badge>
                <Badge variant="outline">Modelo: {result.model}</Badge>
                <Badge variant={result.embeddings_available ? 'success' : 'secondary'}>
                  {result.embeddings_available ? 'RAG semántico' : 'RAG por palabras clave'}
                </Badge>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  <Bot className="size-3.5" /> Respuesta
                </div>
                <p className="whitespace-pre-wrap text-sm text-neutral-800">{result.reply}</p>
              </div>
            </div>
          )}
          {!result && !test.isPending && (
            <p className="text-sm text-neutral-400">Escribe una pregunta para ver la respuesta y el contexto que usaría.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileSearch className="size-4" /> Contexto recuperado</CardTitle>
          <CardDescription>Fragmentos de la base de conocimiento que se inyectan en el modelo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {result?.passages.length === 0 && <p className="text-sm text-neutral-400">No se recuperó contexto para esta consulta.</p>}
          {result?.passages.map((p, i) => (
            <div key={i} className="rounded-lg border border-neutral-200 p-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold text-neutral-700">{p.title || 'Documento'}</span>
                <Badge variant="secondary" className="shrink-0">{(p.score * 100).toFixed(0)}%</Badge>
              </div>
              <p className="line-clamp-4 text-xs text-neutral-500">{p.content}</p>
            </div>
          ))}
          {!result && <p className="text-sm text-neutral-400">—</p>}
        </CardContent>
      </Card>
    </div>
  );
}
